import { useState, useEffect, useRef, useCallback } from "react";
import QRCode from "qrcode";
import jsQR from "jsqr";
import { supabase } from "./supabase";

// Dine together: factor a dining partner's taste into tonight's recommendation.
//
// How it works:
// - The partner taps "Show my code". Davino makes a 6-character code (good for 15 minutes)
//   and shows it as a QR code. Showing it is the consent step: it lets one person use your
//   taste profile for recommendations. It never shares your account.
// - You tap "Scan their code" and photograph it (or type it). From then on they're a
//   saved dining partner: one tap on their name turns their taste on for the evening.
// - "On" lasts 3 hours, then switches itself off, so it never lingers into a solo dinner.
// Sharing is one-way. If both people want it, each shows their code once.
// Database side lives in supabase/dine-together.sql.

// Two people, drawn in the same line style as the Invite icon in the header.
function PeopleIcon() {
  return (
    <svg width="15" height="14" viewBox="0 0 24 22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="9" cy="7" r="4" />
      <path d="M2 20c0-3.9 3.1-7 7-7s7 3.1 7 7" />
      <path d="M16 3.3a4 4 0 0 1 0 7.5" />
      <path d="M19 13.5c1.8.9 3 2.9 3 5.2v1.3" />
    </svg>
  );
}

const ACTIVE_KEY = "davino_dining_with";
const ACTIVE_MS = 3 * 60 * 60 * 1000;
const PAIR_URL = "https://davino.ai/?pair=";

function readActive() {
  try {
    const saved = JSON.parse(localStorage.getItem(ACTIVE_KEY) || "null");
    if (saved && Array.isArray(saved.ids) && saved.until > Date.now()) return saved;
  } catch (e) {}
  return { ids: [], until: 0 };
}

function writeActive(ids) {
  const value = { ids, until: ids.length ? Date.now() + ACTIVE_MS : 0 };
  try { localStorage.setItem(ACTIVE_KEY, JSON.stringify(value)); } catch (e) {}
  return value;
}

// Pull the pairing code out of whatever a QR code or the typed field contains.
function extractCode(text) {
  if (!text) return "";
  const trimmed = String(text).trim();
  const fromUrl = trimmed.match(/[?&]pair=([A-Za-z0-9-]+)/);
  const raw = fromUrl ? fromUrl[1] : trimmed;
  return raw.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
}

// Read a QR code from a photo. Tries a couple of sizes, since a phone screen photographed
// at an angle sometimes reads better smaller.
function decodeQrFromFile(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onerror = () => resolve(null);
    reader.onload = (ev) => {
      const img = new Image();
      img.onerror = () => resolve(null);
      img.onload = () => {
        for (const maxSide of [1200, 800, 1600]) {
          const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
          const w = Math.round(img.width * scale);
          const h = Math.round(img.height * scale);
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, w, h);
          const data = ctx.getImageData(0, 0, w, h);
          const result = jsQR(data.data, w, h);
          if (result && result.data) return resolve(result.data);
        }
        resolve(null);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  });
}

const s = {
  row: { marginTop: "16px" },
  chips: { display: "flex", flexWrap: "wrap", gap: "8px" },
  chip: {
    border: "1px solid #333",
    background: "none",
    color: "#aaa",
    borderRadius: "8px",
    padding: "5px 10px",
    fontSize: "12px",
    cursor: "pointer",
    fontFamily: "inherit"
  },
  chipOn: { background: "#8B4513", borderColor: "#8B4513", color: "#fff" },
  addPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    background: "none",
    border: "1px solid #333",
    borderRadius: "8px",
    color: "#aaa",
    fontSize: "12px",
    padding: "5px 10px",
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap"
  },
  hint: { fontSize: "12px", color: "#777", marginTop: "8px" },
  overlay: {
    position: "fixed", inset: 0, backgroundColor: "rgba(0, 0, 0, 0.88)", zIndex: 1000,
    display: "flex", alignItems: "center", justifyContent: "center", padding: "20px"
  },
  sheet: {
    position: "relative", width: "100%", maxWidth: "400px", maxHeight: "90vh", overflowY: "auto",
    backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: "20px",
    padding: "28px 22px 22px", color: "#f0f0f0",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    textAlign: "center", boxSizing: "border-box"
  },
  close: {
    position: "absolute", top: "10px", right: "10px", width: "36px", height: "36px",
    border: "none", borderRadius: "50%", background: "#262626", color: "#aaa",
    fontSize: "20px", lineHeight: "36px", cursor: "pointer", fontFamily: "inherit"
  },
  title: { fontSize: "22px", fontWeight: "600", color: "#fff", margin: "0 0 6px" },
  sub: { fontSize: "15px", color: "#999", margin: "0 0 18px", lineHeight: "1.5" },
  tabs: { display: "flex", gap: "8px", marginBottom: "18px" },
  tab: {
    flex: 1, padding: "10px", borderRadius: "10px", border: "1px solid #333",
    background: "none", color: "#aaa", fontSize: "14px", cursor: "pointer", fontFamily: "inherit"
  },
  tabOn: { background: "#262626", color: "#fff", borderColor: "#444" },
  input: {
    width: "100%", boxSizing: "border-box", backgroundColor: "#111", border: "1px solid #333",
    borderRadius: "10px", color: "#f0f0f0", fontSize: "16px", padding: "12px",
    fontFamily: "inherit", marginBottom: "12px", textAlign: "center"
  },
  primary: {
    width: "100%", backgroundColor: "#8B4513", color: "#fff", border: "none", borderRadius: "12px",
    fontSize: "16px", fontWeight: "600", padding: "14px", cursor: "pointer", fontFamily: "inherit"
  },
  secondary: {
    width: "100%", backgroundColor: "transparent", color: "#aaa", border: "1px solid #333",
    borderRadius: "12px", fontSize: "15px", padding: "12px", cursor: "pointer",
    fontFamily: "inherit", marginTop: "10px"
  },
  disabled: { opacity: 0.5, cursor: "not-allowed" },
  qrCard: {
    backgroundColor: "#fff", borderRadius: "16px", padding: "18px", width: "240px",
    maxWidth: "100%", margin: "0 auto 12px", boxSizing: "border-box"
  },
  code: { fontSize: "28px", letterSpacing: "6px", fontWeight: "600", color: "#fff", margin: "4px 0 6px" },
  small: { fontSize: "13px", color: "#888", lineHeight: "1.5" },
  ok: { fontSize: "14px", color: "#9fd49f", marginTop: "12px", lineHeight: "1.5" },
  err: { fontSize: "14px", color: "#ff6b6b", marginTop: "12px", lineHeight: "1.5" },
  divider: { borderTop: "1px solid #2a2a2a", margin: "22px 0 14px" },
  manageTitle: { fontSize: "12px", color: "#777", textTransform: "uppercase", letterSpacing: "1px", textAlign: "left", marginBottom: "6px" },
  manageRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", fontSize: "15px", color: "#ccc" },
  manageBtn: { background: "none", border: "none", color: "#888", fontSize: "13px", textDecoration: "underline", cursor: "pointer", fontFamily: "inherit" }
};

export default function DiningTogether({ userId, displayName, labelStyle, initialCode, onCompanionsChange, onDisplayNameSaved, logEvent }) {
  const [available, setAvailable] = useState(false); // false until the database side answers
  const [partners, setPartners] = useState([]);
  const [sharers, setSharers] = useState([]);
  const [activeIds, setActiveIds] = useState(() => readActive().ids);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [mode, setMode] = useState("scan"); // 'scan' | 'show'
  const [nameInput, setNameInput] = useState(displayName || "");
  const [code, setCode] = useState("");
  const [codeExpiresAt, setCodeExpiresAt] = useState(0);
  const [qrSvg, setQrSvg] = useState("");
  const [typedCode, setTypedCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState({ kind: "", text: "" });
  const [, forceTick] = useState(0);
  const fileRef = useRef(null);
  const initialHandled = useRef(false);

  const loadPartners = useCallback(async () => {
    if (!userId) return [];
    const { data, error } = await supabase.rpc("get_dining_partners");
    if (error) {
      // Most likely the database script hasn't been run yet. Stay out of the way.
      console.error("Dining partners unavailable:", error);
      return null;
    }
    setAvailable(true);
    setPartners(data || []);
    const { data: sharerData } = await supabase.rpc("get_taste_sharers");
    setSharers(sharerData || []);
    return data || [];
  }, [userId]);

  useEffect(() => { loadPartners(); }, [loadPartners]);

  useEffect(() => { if (displayName && !nameInput) setNameInput(displayName); }, [displayName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Arriving from a pairing link (davino.ai/?pair=CODE): open the sheet with the code filled in.
  useEffect(() => {
    if (!initialCode || initialHandled.current || !available) return;
    initialHandled.current = true;
    setTypedCode(extractCode(initialCode));
    setMode("scan");
    setSheetOpen(true);
  }, [initialCode, available]);

  // Tell the app whose taste is on, and switch everyone off when the 3 hours are up.
  useEffect(() => {
    const ids = activeIds.filter(id => partners.some(p => p.partner_id === id));
    onCompanionsChange(
      partners
        .filter(p => ids.includes(p.partner_id))
        .map(p => ({ name: p.display_name || "your dining partner", profile: p.profile }))
    );
    const { until } = readActive();
    if (!ids.length || !until) return;
    const timer = setTimeout(() => setActiveIds([]), Math.max(0, until - Date.now()));
    return () => clearTimeout(timer);
  }, [activeIds, partners]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the "expires in" line honest while a code is showing.
  useEffect(() => {
    if (!code) return;
    const t = setInterval(() => forceTick(n => n + 1), 15000);
    return () => clearInterval(t);
  }, [code]);

  useEffect(() => {
    if (!sheetOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [sheetOpen]);

  if (!available) return null;

  function setActive(ids) {
    writeActive(ids);
    setActiveIds(ids);
  }

  function toggle(partnerId) {
    if (activeIds.includes(partnerId)) {
      setActive(activeIds.filter(id => id !== partnerId));
    } else {
      setActive([...activeIds, partnerId]);
      logEvent && logEvent("dining_together_on", { partners: activeIds.length + 1 });
    }
  }

  async function saveName() {
    const name = nameInput.trim();
    if (!name || name === displayName) return;
    const { error } = await supabase.from("profiles").update({ display_name: name.slice(0, 40) }).eq("user_id", userId);
    if (!error && onDisplayNameSaved) onDisplayNameSaved(name.slice(0, 40));
  }

  function openSheet(startMode) {
    setMode(startMode);
    setMessage({ kind: "", text: "" });
    setSheetOpen(true);
    logEvent && logEvent("dining_sheet_opened");
  }

  function closeSheet() {
    setSheetOpen(false);
    setCode("");
    setQrSvg("");
    setTypedCode("");
    setMessage({ kind: "", text: "" });
  }

  async function showMyCode() {
    if (!nameInput.trim()) {
      setMessage({ kind: "err", text: "Add your first name so they know whose taste it is." });
      return;
    }
    setBusy(true);
    setMessage({ kind: "", text: "" });
    try {
      await saveName();
      const { data, error } = await supabase.rpc("create_dining_code", { p_display_name: nameInput.trim() });
      if (error || !data) throw error || new Error("no code");
      const svg = await QRCode.toString(PAIR_URL + data, { type: "svg", margin: 0, errorCorrectionLevel: "M", color: { dark: "#141414", light: "#ffffff" } });
      setCode(data);
      setQrSvg(svg);
      setCodeExpiresAt(Date.now() + 15 * 60 * 1000);
      logEvent && logEvent("dining_code_shown");
    } catch (e) {
      console.error("create_dining_code failed:", e);
      setMessage({ kind: "err", text: "Couldn't make a code. Check your signal and try again." });
    } finally {
      setBusy(false);
    }
  }

  async function redeem(rawCode) {
    const clean = extractCode(rawCode);
    if (clean.length < 6) {
      setMessage({ kind: "err", text: "That doesn't look like a Davino code. It's 6 letters and numbers." });
      return;
    }
    setBusy(true);
    setMessage({ kind: "", text: "" });
    try {
      if (nameInput.trim()) await saveName();
      const { data, error } = await supabase.rpc("redeem_dining_code", { p_code: clean });
      if (error) throw error;
      if (data && data.error === "self") {
        setMessage({ kind: "err", text: "That's your own code. Have your dining partner show theirs." });
        return;
      }
      if (!data || data.error) {
        setMessage({ kind: "err", text: "That code didn't work. It may have expired, so ask them for a fresh one." });
        return;
      }
      await loadPartners();
      if (!activeIds.includes(data.partner_id)) setActive([...activeIds, data.partner_id]);
      setTypedCode("");
      const name = data.display_name || "Your dining partner";
      setMessage({ kind: "ok", text: `You're paired. ${name}'s taste is on for the next 3 hours, and next time it's one tap.` });
      logEvent && logEvent("dining_partner_paired");
    } catch (e) {
      console.error("redeem_dining_code failed:", e);
      setMessage({ kind: "err", text: "Couldn't connect. Check your signal and try again." });
    } finally {
      setBusy(false);
    }
  }

  async function handlePhoto(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    setMessage({ kind: "", text: "" });
    const text = await decodeQrFromFile(file);
    setBusy(false);
    if (!text) {
      setMessage({ kind: "err", text: "Couldn't read a code in that photo. Try again a little closer, or type the 6 characters under it." });
      return;
    }
    redeem(text);
  }

  async function removePartner(partnerId) {
    await supabase.rpc("remove_dining_partner", { p_partner: partnerId });
    setActive(activeIds.filter(id => id !== partnerId));
    loadPartners();
  }

  async function stopSharing(otherUserId) {
    await supabase.rpc("stop_sharing_taste", { p_user: otherUserId });
    loadPartners();
  }

  const minutesLeft = code ? Math.max(0, Math.ceil((codeExpiresAt - Date.now()) / 60000)) : 0;
  const anyOn = activeIds.some(id => partners.some(p => p.partner_id === id));

  return (
    <div style={s.row}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
        <label style={{ ...labelStyle, marginBottom: 0 }}>Dining with</label>
        <button style={s.addPill} onClick={() => openSheet("scan")}>
          <PeopleIcon />
          {partners.length > 0 ? "Add another" : "Add another Davino user"}
        </button>
      </div>
      {partners.length > 0 && (
        <div style={{ ...s.chips, marginTop: "8px" }}>
          {partners.map(p => {
            const on = activeIds.includes(p.partner_id);
            return (
              <button
                key={p.partner_id}
                style={{ ...s.chip, ...(on ? s.chipOn : {}) }}
                onClick={() => toggle(p.partner_id)}
                aria-pressed={on}
              >
                {on ? "✓ " : ""}{p.display_name || "Dining partner"}
              </button>
            );
          })}
        </div>
      )}
      {anyOn && (
        <div style={s.hint}>
          Picks will suit {activeIds.filter(id => partners.some(p => p.partner_id === id)).length > 1 ? "everyone at the table" : "you both"}. Switches off on its own after 3 hours.
        </div>
      )}

      {sheetOpen && (
        <div style={s.overlay} onClick={closeSheet}>
          <div style={s.sheet} onClick={e => e.stopPropagation()} role="dialog" aria-label="Dine together">
            <button style={s.close} onClick={closeSheet} aria-label="Close">×</button>
            <div style={s.title}>Dine together</div>
            <div style={s.sub}>Davino picks bottles you'll both enjoy, using both of your palates.</div>

            <div style={s.tabs}>
              <button style={{ ...s.tab, ...(mode === "scan" ? s.tabOn : {}) }} onClick={() => { setMode("scan"); setMessage({ kind: "", text: "" }); }}>
                Scan their code
              </button>
              <button style={{ ...s.tab, ...(mode === "show" ? s.tabOn : {}) }} onClick={() => { setMode("show"); setMessage({ kind: "", text: "" }); }}>
                Show my code
              </button>
            </div>

            {!displayName && (
              <input
                style={s.input}
                placeholder="Your first name"
                value={nameInput}
                onChange={e => setNameInput(e.target.value)}
                autoComplete="given-name"
              />
            )}

            {mode === "show" && (
              <>
                {code && minutesLeft > 0 ? (
                  <>
                    <div style={s.qrCard}>
                      <div
                        style={{ width: "100%", lineHeight: 0 }}
                        role="img"
                        aria-label={`Davino pairing code ${code}`}
                        dangerouslySetInnerHTML={{ __html: qrSvg }}
                      />
                    </div>
                    <div style={s.code}>{code}</div>
                    <div style={s.small}>
                      On their phone: open Davino, tap Add another Davino user, then Scan their code.<br />
                      Good for {minutesLeft} more minute{minutesLeft === 1 ? "" : "s"}.
                    </div>
                    <div style={{ ...s.small, marginTop: "12px" }}>
                      This lets them use your taste profile for recommendations. It doesn't give them your account.
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ ...s.small, marginBottom: "14px" }}>
                      Show this to the person you're dining with. It lets their Davino use your taste profile for recommendations. It doesn't give them your account.
                    </div>
                    <button style={{ ...s.primary, ...(busy ? s.disabled : {}) }} disabled={busy} onClick={showMyCode}>
                      {busy ? "Making your code…" : code ? "Get a new code" : "Show my code"}
                    </button>
                  </>
                )}
              </>
            )}

            {mode === "scan" && (
              <>
                <button style={{ ...s.primary, ...(busy ? s.disabled : {}) }} disabled={busy} onClick={() => fileRef.current && fileRef.current.click()}>
                  {busy ? "Reading…" : "📷 Take a photo of their code"}
                </button>
                <input ref={fileRef} type="file" accept="image/*" onChange={handlePhoto} style={{ display: "none" }} />
                <div style={{ ...s.small, margin: "16px 0 8px" }}>or type the 6 characters under it</div>
                <input
                  style={{ ...s.input, letterSpacing: "4px", textTransform: "uppercase" }}
                  placeholder="K7MP2Q"
                  value={typedCode}
                  onChange={e => setTypedCode(e.target.value)}
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  maxLength={12}
                />
                <button
                  style={{ ...s.secondary, ...(busy || !typedCode.trim() ? s.disabled : {}) }}
                  disabled={busy || !typedCode.trim()}
                  onClick={() => redeem(typedCode)}
                >
                  Pair
                </button>
              </>
            )}

            {message.text && <div style={message.kind === "ok" ? s.ok : s.err}>{message.text}</div>}

            {(partners.length > 0 || sharers.length > 0) && <div style={s.divider} />}
            {partners.length > 0 && (
              <div style={{ marginBottom: "12px" }}>
                <div style={s.manageTitle}>Your dining partners</div>
                {partners.map(p => (
                  <div key={p.partner_id} style={s.manageRow}>
                    <span>{p.display_name || "Dining partner"}</span>
                    <button style={s.manageBtn} onClick={() => removePartner(p.partner_id)}>Remove</button>
                  </div>
                ))}
              </div>
            )}
            {sharers.length > 0 && (
              <div>
                <div style={s.manageTitle}>Can use your taste</div>
                {sharers.map(u => (
                  <div key={u.user_id} style={s.manageRow}>
                    <span>{u.display_name || "Another Davino user"}</span>
                    <button style={s.manageBtn} onClick={() => stopSharing(u.user_id)}>Stop sharing</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
